// FieldIQ — Lookup tab — rep/district lookup with offline cache

import { useState, useEffect, useRef } from "react";
import {
  MessageSquare,
  Users,
  Loader2,
  Mail,
  MapPin,
  Search,
  History,
  Navigation,
  Heart,
  Phone,
  Check,
  X,
  ExternalLink,
  WifiOff,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Avatar } from "../components/ui";
import { FIELDIQ_API_URL, authFetch, getToken } from "../lib/api";
import {
  lookupCacheClear,
  lookupCacheList,
  lookupCachePut,
} from "../lib/offline";

const PARTY_BADGE = {
  R: "bg-red-900/60 text-red-300 border border-red-700",
  D: "bg-blue-900/60 text-blue-300 border border-blue-700",
};

// Module-level ref so VoterInfoPanel can call it without prop drilling

const RepCard = ({ rep, chamber, onAskAbout }) => {
  const initials = rep.name
    ? rep.name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

  return (
    <div className="bg-slate-800 rounded-xl p-4 flex gap-3 items-start">
      {rep.image_url ? (
        <img
          src={rep.image_url}
          alt={rep.name}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0 bg-slate-700"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        />
      ) : null}
      <div
        className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center
                   text-slate-300 font-semibold text-sm flex-shrink-0"
        style={{ display: rep.image_url ? "none" : "flex" }}
      >
        {initials}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onAskAbout?.(rep.name)}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-100 hover:text-cyan-400 transition-colors text-left"
          >
            <MessageSquare size={12} className="text-cyan-500 flex-shrink-0" />
            {rep.name}
          </button>
          {rep.party && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                PARTY_BADGE[rep.party] ||
                "bg-slate-700 text-slate-300 border border-slate-600"
              }`}
            >
              {rep.party}
            </span>
          )}
        </div>
        <p className="text-slate-400 text-xs mt-0.5">{rep.office || chamber}</p>

        <div className="flex gap-2 mt-2 flex-wrap">
          {rep.phone && (
            <a
              href={`tel:${rep.phone.replace(/\D/g, "")}`}
              className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 transition-colors"
              aria-label={`Call ${rep.name}`}
            >
              <Phone size={12} />
              {rep.phone}
            </a>
          )}
          {rep.email && (
            <a
              href={`mailto:${rep.email}`}
              className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 transition-colors"
              aria-label={`Email ${rep.name}`}
            >
              <Mail size={12} />
              Email
            </a>
          )}
          {rep.website && (
            <a
              href={rep.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 transition-colors"
              aria-label={`${rep.name}'s website`}
            >
              <ExternalLink size={12} />
              Website
            </a>
          )}
          {rep.capitol_address && (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(rep.capitol_address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
              aria-label={`Open ${rep.name}'s capitol office in Maps`}
            >
              <MapPin size={12} />
              Capitol
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Voting locations near the looked-up address (vote centers / drop boxes).
// List only — each address deep-links to Google Maps; no embedded map.
const VOTE_TYPE_LABEL = {
  vote_center: "Vote Center",
  drop_box: "Drop Box",
  drop_box_24h: "24-Hour Drop Box",
};

const VoteCenterRow = ({ c }) => (
  <li className="pb-3 border-b border-slate-700 last:border-0 last:pb-0">
    <div className="flex items-start justify-between gap-2">
      <p className="text-slate-100 text-sm font-semibold">{c.name}</p>
      {typeof c.miles === "number" && (
        <span className="text-xs text-slate-500 whitespace-nowrap flex-shrink-0">
          {c.miles} mi
        </span>
      )}
    </div>
    <div className="flex items-center gap-2 flex-wrap mt-0.5">
      <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-slate-700 text-slate-300">
        {VOTE_TYPE_LABEL[c.type] || c.type_label || "Voting Location"}
      </span>
      {c.hours && <span className="text-xs text-slate-400">{c.hours}</span>}
    </div>
    <a
      href={`https://maps.google.com/?q=${encodeURIComponent(c.address)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors mt-1"
    >
      <Navigation size={12} className="flex-shrink-0" />
      {c.address}
      <ExternalLink size={11} className="flex-shrink-0 opacity-70" />
    </a>
  </li>
);

const VoteCentersCard = ({ centers }) => {
  const [expanded, setExpanded] = useState(false);
  if (!centers || centers.length === 0) return null;
  // Collapsed by default to the single closest location; the rest expand.
  const shown = expanded ? centers : centers.slice(0, 1);
  const hidden = centers.length - 1;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
      <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-3 flex items-center gap-1.5">
        <MapPin size={13} className="text-cyan-500" />
        Where to Vote — Nearest Locations
      </h3>
      <ul className="space-y-3">
        {shown.map((c, i) => (
          <VoteCenterRow key={`${c.address}-${i}`} c={c} />
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-3 flex items-center gap-1 text-xs font-medium text-sky-400 hover:text-sky-300 transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp size={14} className="flex-shrink-0" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown size={14} className="flex-shrink-0" />
              Show {hidden} more nearby
            </>
          )}
        </button>
      )}
    </div>
  );
};

const DistrictChip = ({ label, value }) =>
  value ? (
    <div className="bg-slate-800 rounded-lg px-3 py-2 text-center min-w-0">
      <p className="text-slate-400 text-xs">{label}</p>
      <p className="text-slate-100 font-semibold text-sm truncate">
        {value.number ? `#${value.number}` : "–"}
      </p>
      {value.name && (
        <p className="text-slate-500 text-xs truncate">{value.name}</p>
      )}
    </div>
  ) : null;

const EXEC_ROLE_LABEL = {
  governor: "Governor",
  us_senator: "U.S. Senator",
  attorney_general: "Attorney General",
  secretary_of_state: "Secretary of State",
  state_treasurer: "State Treasurer",
  superintendent_of_public_instruction: "Superintendent of Public Instruction",
  state_mine_inspector: "State Mine Inspector",
};

const EXEC_ROLE_ORDER = Object.keys(EXEC_ROLE_LABEL);

const REP_LEVEL = {
  us_house: "federal",
  state_house: "state",
  state_senate: "state",
  school_board: "local",
};

const EXEC_LEVEL = {
  governor: "state",
  us_senator: "federal",
  attorney_general: "state",
  secretary_of_state: "state",
  state_treasurer: "state",
  superintendent_of_public_instruction: "state",
  state_mine_inspector: "state",
};

const DISTRICT_LEVEL = {
  state_house: "state",
  state_senate: "state",
  congressional: "federal",
  school_unified: "local",
};

const LookupResult = ({ data, onSave, saved, onAskAbout, facilityId }) => {
  const {
    districts = {},
    representatives = {},
    executives = {},
    candidates = [],
    precinct_captain: captain = null,
    vote_centers: voteCenters = [],
  } = data;

  const precinct = districts.voting_precinct || null;

  const [activeTab, setActiveTab] = useState("current");
  const [levelFilter, setLevelFilter] = useState("all");
  const [precinctMembers, setPrecinctMembers] = useState(null);
  const [precinctMembersLoading, setPrecinctMembersLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== "precinct" || !precinct) return;
    if (precinctMembers !== null) return;
    setPrecinctMembersLoading(true);
    authFetch(
      `/fieldiq/precinct-members?precinct_number=${encodeURIComponent(precinct.number)}${precinct.name ? `&precinct_name=${encodeURIComponent(precinct.name)}` : ""}${facilityId ? `&facility_id=${encodeURIComponent(facilityId)}` : ""}`,
    )
      .then((data) => setPrecinctMembers(data?.members || []))
      .catch(() => setPrecinctMembers([]))
      .finally(() => setPrecinctMembersLoading(false));
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const allRepSections = [
    { key: "state_house", label: "State House" },
    { key: "state_senate", label: "State Senate" },
    { key: "us_house", label: "U.S. House" },
    { key: "school_board", label: "School Board" },
  ];

  const repSections = allRepSections.filter(
    ({ key }) => levelFilter === "all" || REP_LEVEL[key] === levelFilter,
  );

  const execSections = EXEC_ROLE_ORDER.filter(
    (role) =>
      executives[role]?.length &&
      (levelFilter === "all" || EXEC_LEVEL[role] === levelFilter),
  ).map((role) => ({
    role,
    label: EXEC_ROLE_LABEL[role],
    officials: executives[role],
  }));

  const allDistrictEntries = [
    { key: "state_house", label: "State House", value: districts.state_house },
    {
      key: "state_senate",
      label: "State Senate",
      value: districts.state_senate,
    },
    { key: "congressional", label: "Congress", value: districts.congressional },
    {
      key: "school_unified",
      label: "School District",
      value: districts.school_unified,
    },
  ];

  const districtEntries = allDistrictEntries.filter(
    ({ key, value }) =>
      value && (levelFilter === "all" || DISTRICT_LEVEL[key] === levelFilter),
  );

  const LEVEL_FILTERS = [
    { id: "all", label: "All" },
    { id: "federal", label: "Federal" },
    { id: "state", label: "State" },
    { id: "local", label: "Local" },
  ];

  // Group candidates into the same chambers the Current tab uses (+ Statewide),
  // so the Candidates view mirrors its groupings and honors the level pills.
  const CANDIDATE_SECTIONS = [
    { key: "us_house", label: "U.S. House", level: "federal" },
    { key: "state_senate", label: "State Senate", level: "state" },
    { key: "state_house", label: "State House", level: "state" },
    { key: "statewide", label: "Statewide", level: "state" },
  ];
  const candidateSectionKey = (c) => {
    const o = (c.office || "").toLowerCase();
    if (o.includes("u.s. house") || o.includes("us house") || o.includes("congress")) return "us_house";
    if (o.includes("state senate")) return "state_senate";
    if (o.includes("state house")) return "state_house";
    return "statewide";
  };
  const candidatesBySection = {};
  candidates.forEach((c) => {
    const k = candidateSectionKey(c);
    (candidatesBySection[k] = candidatesBySection[k] || []).push(c);
  });
  const candidateSections = CANDIDATE_SECTIONS.filter(
    (s) =>
      (candidatesBySection[s.key] || []).length &&
      (levelFilter === "all" || s.level === levelFilter),
  );

  return (
    <div className="space-y-4">
      {/* Precinct card — shown first when precinct data is available */}
      {precinct && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
            Voting Precinct
          </h3>
          <button
            onClick={() =>
              onAskAbout?.(`Precinct ${precinct.name || precinct.number}`)
            }
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-100 hover:text-cyan-400 transition-colors text-left"
          >
            <MessageSquare size={12} className="text-cyan-500 flex-shrink-0" />
            {precinct.name || `Precinct ${precinct.number}`}
          </button>
          {captain && (
            <div className="mt-3 pt-3 border-t border-slate-700 space-y-1">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">
                Precinct Captain
              </p>
              <p className="text-slate-200 text-sm font-medium">
                {captain.name}
              </p>
              {captain.title && (
                <p className="text-slate-400 text-xs">{captain.title}</p>
              )}
              <div className="flex gap-3 mt-2">
                {captain.phone && (
                  <a
                    href={`tel:${captain.phone}`}
                    className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300"
                  >
                    <Phone size={12} />
                    {captain.phone}
                  </a>
                )}
                {captain.email && (
                  <a
                    href={`mailto:${captain.email}`}
                    className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300"
                  >
                    <Mail size={12} />
                    {captain.email}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Voting locations — directly below the precinct card */}
      <VoteCentersCard centers={voteCenters} />

      {/* Districts summary — above the pills so the location's districts read
          first, before the level filter + the Candidates/Current/Precinct tabs. */}
      {districtEntries.length > 0 && (
        <div>
          <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
            Districts
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {districtEntries.map(({ key, label, value }) => (
              <DistrictChip key={key} label={label} value={value} />
            ))}
          </div>
        </div>
      )}

      {/* Level filter pills — shared across all tabs */}
      <div className="flex gap-2">
        {LEVEL_FILTERS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setLevelFilter(id)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              levelFilter === id
                ? "bg-sky-700 text-sky-100"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tabs: Candidates | Current | Precinct */}
      <div className="flex border-b border-slate-700">
        {[
          { id: "candidates", label: "Candidates" },
          { id: "current", label: "Current" },
          ...(precinct ? [{ id: "precinct", label: "Precinct" }] : []),
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === id
                ? "text-sky-400 border-b-2 border-sky-500 -mb-px"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Candidates tab */}
      {activeTab === "candidates" && (
        <>
          {candidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
              <Users size={32} className="text-slate-700" />
              <p className="text-slate-400 text-sm font-medium">
                No candidates yet
              </p>
              <p className="text-slate-500 text-xs">
                Candidate data for this location will appear here.
              </p>
            </div>
          ) : candidateSections.length === 0 ? (
            <p className="text-slate-500 text-sm italic px-1 py-6 text-center">
              No candidates at this level.
            </p>
          ) : (
            candidateSections.map((s) => (
              <div key={s.key}>
                <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
                  {s.label}
                </h3>
                <div className="space-y-2">
                  {candidatesBySection[s.key].map((c) => (
                    <div
                      key={c.id}
                      className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 space-y-1"
                    >
                      <div className="flex items-start gap-3">
                        <Avatar
                          name={c.full_name}
                          photoUrl={c.image_url || null}
                          size={10}
                          colorClass="from-slate-500 to-slate-600"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold text-white leading-snug">
                              {c.full_name}
                            </p>
                            {c.party && (
                              <span
                                className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                                  PARTY_BADGE[c.party] ||
                                  "bg-slate-700 text-slate-300 border border-slate-600"
                                }`}
                              >
                                {c.party}
                              </span>
                            )}
                          </div>
                          {c.office && (
                            <p className="text-xs text-slate-400">{c.office}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 pt-1">
                        {c.election_date && (
                          <span className="text-xs text-slate-500">
                            {new Date(c.election_date).toLocaleDateString(
                              undefined,
                              { month: "short", day: "numeric", year: "numeric" },
                            )}
                          </span>
                        )}
                        {c.website_url && (
                          <a
                            href={c.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300"
                          >
                            <ExternalLink size={11} />
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* Current tab */}
      {activeTab === "current" && (
        <>
          {/* Representatives */}
          {repSections.map(({ key, label }) => {
            const reps = representatives[key];
            if (reps === null || reps === undefined) return null;
            return (
              <div key={key}>
                <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
                  {label}
                </h3>
                {reps.length === 0 ? (
                  <p className="text-slate-500 text-sm italic px-1">
                    No {label.toLowerCase()} records loaded yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {reps.map((rep, i) => (
                      <RepCard
                        key={i}
                        rep={rep}
                        chamber={label}
                        onAskAbout={onAskAbout}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Executives */}
          {execSections.map(({ role, label, officials }) => (
            <div key={role}>
              <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
                {label}
              </h3>
              <div className="space-y-2">
                {officials.map((rep, i) => (
                  <RepCard
                    key={i}
                    rep={rep}
                    chamber={label}
                    onAskAbout={onAskAbout}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Save action */}
          <button
            onClick={onSave}
            disabled={saved}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              saved
                ? "bg-emerald-900/50 text-emerald-400 border border-emerald-700 cursor-default"
                : "bg-slate-700 text-slate-200 hover:bg-slate-600 active:bg-slate-500"
            }`}
            aria-label={
              saved ? "Saved to address book" : "Save to address book"
            }
          >
            {saved ? <Check size={14} /> : <Heart size={14} />}
            {saved ? "Saved to address book" : "Save to address book"}
          </button>
        </>
      )}

      {/* Precinct tab */}
      {activeTab === "precinct" && (
        <div className="space-y-3">
          {precinctMembersLoading && (
            <div className="flex items-center justify-center py-10 gap-3 text-slate-500">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">Loading members…</span>
            </div>
          )}
          {!precinctMembersLoading && precinctMembers?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
              <Users size={32} className="text-slate-700" />
              <p className="text-slate-400 text-sm font-medium">
                No members yet
              </p>
              <p className="text-slate-500 text-xs">
                No FieldIQ members are aligned to this precinct yet.
              </p>
            </div>
          )}
          {!precinctMembersLoading && precinctMembers?.length > 0 && (
            <>
              <p className="text-xs text-slate-500">
                {precinctMembers.length} member
                {precinctMembers.length !== 1 ? "s" : ""} aligned to{" "}
                {precinct.name || `Precinct ${precinct.number}`}
              </p>
              <div className="space-y-2">
                {precinctMembers.map((member, i) => (
                  <div
                    key={member.id || i}
                    className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 flex items-center gap-3"
                  >
                    <Avatar
                      name={member.name || member.email || "?"}
                      photoUrl={member.photo_url || null}
                      size={9}
                      colorClass="from-cyan-500 to-blue-600"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {member.name || member.email || "Unknown"}
                      </p>
                      {member.role && (
                        <p className="text-xs text-slate-400">
                          {{
                            pc: "Precinct Captain",
                            ld: "Legislative District",
                            admin: "Admin",
                          }[member.role] || member.role}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const LookupTab = ({
  user,
  facility,
  onAskAbout,
  initialCoords,
  onConsumeCoords,
  isOnline,
}) => {
  const facilityId = facility?.facility_id || null;
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    lookupCacheList().then((items) => setHistory(items.slice(0, 10)));
  }, []);

  useEffect(() => {
    if (!initialCoords) return;
    onConsumeCoords?.();
    doLookupByCoords(initialCoords.lat, initialCoords.lng);
  }, [initialCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  const doLookupByCoords = async (lat, lng) => {
    if (!navigator.onLine) {
      setError("Lookup is not available offline.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setSaved(false);

    const token = getToken();
    try {
      const resp = await fetch(`${FIELDIQ_API_URL}/api/v1/lookup/coords`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          lat,
          lng,
          ...(facilityId ? { facility_id: facilityId } : {}),
        }),
      });
      if (resp.status === 429) {
        setError("Too many lookups — please wait a moment and try again.");
        return;
      }
      if (!resp.ok) {
        setError("Lookup failed. Please try again.");
        return;
      }
      const data = await resp.json();
      setResult(data);
      if (data.address) {
        setQuery(data.address);
        const cacheKey = data.address.trim().toLowerCase();
        await lookupCachePut(cacheKey, data);
        setHistory((prev) =>
          [
            { key: cacheKey, payload: data, cachedAt: Date.now() },
            ...prev,
          ].slice(0, 10),
        );
      }
    } catch {
      setError("Network error — check your connection and retry.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      return;
    }
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => doLookupByCoords(pos.coords.latitude, pos.coords.longitude),
      () => {
        setLoading(false);
        setError(
          "Location access denied. Enable location permissions and try again.",
        );
      },
      { timeout: 10000, maximumAge: 60000 },
    );
  };

  const doLookup = async (address) => {
    if (!address.trim()) return;
    if (!navigator.onLine) {
      setError("Lookup is not available offline.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setSaved(false);

    // Network call
    const token = getToken();
    const url = `${FIELDIQ_API_URL}/api/v1/lookup/address?address=${encodeURIComponent(address.trim())}${facilityId ? `&facility_id=${encodeURIComponent(facilityId)}` : ""}`;
    try {
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.status === 404) {
        const body = await resp.json().catch(() => ({}));
        setError(
          body.detail || "Address not found. Try adding city and state.",
        );
        return;
      }
      if (resp.status === 429) {
        setError("Too many lookups — please wait a moment and try again.");
        return;
      }
      if (!resp.ok) {
        setError("Lookup failed. Please try again.");
        return;
      }
      const data = await resp.json();
      setResult(data);
    } catch {
      setError("Network error — check your connection and retry.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowHistory(false);
    doLookup(query);
  };

  const handleHistorySelect = (item) => {
    setQuery(item.key);
    setResult(item.payload);
    setSaved(false);
    setShowHistory(false);
  };

  const handleSave = async () => {
    if (!result) return;
    const key =
      result.address?.trim().toLowerCase() || query.trim().toLowerCase();
    await lookupCachePut(key, result);
    setSaved(true);
  };

  return (
    <div className="flex-1 overflow-y-auto bg-slate-950 px-4 py-4 space-y-4">
      <div>
        <h2 className="text-slate-100 font-semibold text-base">
          Address Lookup
        </h2>
        <p className="text-slate-400 text-xs mt-0.5">
          Find your district and representatives
        </p>
      </div>

      {!isOnline && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-900/40 border border-amber-700/50 rounded-xl text-amber-300 text-xs">
          <WifiOff size={13} />
          Lookup requires a connection. Reconnect to search.
        </div>
      )}

      {/* Search form */}
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex gap-2 overflow-hidden">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => history.length > 0 && setShowHistory(true)}
            onBlur={() => setTimeout(() => setShowHistory(false), 150)}
            placeholder="123 Main St, Phoenix, AZ"
            className="min-w-0 flex-1 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5
                       text-slate-100 placeholder-slate-500 text-sm outline-none
                       focus:border-sky-600 transition-colors"
            aria-label="Street address"
            autoComplete="street-address"
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="flex-shrink-0 bg-sky-700 hover:bg-sky-600 active:bg-sky-500 disabled:bg-slate-700
                       disabled:text-slate-500 text-white rounded-xl px-3 py-2.5
                       flex items-center text-sm font-medium transition-colors"
            aria-label="Search"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Search size={16} />
            )}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleUseLocation}
            className="flex-shrink-0 bg-slate-700 hover:bg-slate-600 active:bg-slate-500 disabled:opacity-40
                       text-slate-200 rounded-xl px-3 py-2.5
                       flex items-center text-sm font-medium transition-colors"
            aria-label="Use current location"
            title="Use current location"
          >
            <Navigation size={16} />
          </button>
        </div>

        {/* Recent lookups dropdown */}
        {showHistory && history.length > 0 && (
          <div
            className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700
                          rounded-xl shadow-xl z-10 overflow-hidden"
          >
            {history.map((item, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={() => handleHistorySelect(item)}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-300
                           hover:bg-slate-700 transition-colors border-b border-slate-700/50 last:border-0"
              >
                <History size={12} className="inline mr-2 text-slate-500" />
                {item.payload?.address || item.key}
              </button>
            ))}
            <button
              type="button"
              onMouseDown={async () => {
                await lookupCacheClear();
                setHistory([]);
                setShowHistory(false);
              }}
              className="w-full text-center px-4 py-2 text-xs text-slate-500
                         hover:text-slate-300 hover:bg-slate-700/50 transition-colors"
            >
              Clear search history
            </button>
          </div>
        )}
      </form>

      {/* Error state */}
      {error && (
        <div
          className="bg-red-950/50 border border-red-800 rounded-xl px-4 py-3
                     text-red-300 text-sm flex items-start gap-2"
          role="alert"
        >
          <X size={14} className="flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-10 gap-3 text-slate-500">
          <Loader2 size={20} className="animate-spin" />
          <span className="text-sm">Looking up address…</span>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <LookupResult
          data={result}
          onSave={handleSave}
          saved={saved}
          onAskAbout={onAskAbout}
          facilityId={facilityId}
        />
      )}

      {/* Empty state */}
      {!result && !loading && !error && (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
          <MapPin size={32} className="text-slate-700" />
          <p className="text-slate-500 text-sm">
            Enter an address to find your state and local representatives
          </p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PROFILE AVATAR — header top-right, three mandatory visual states
